/**
 * @fileoverview Sidebar UI management including menu interactions, tab persistence, and resizing.
 * @module ui/UiManager
 */


import { SidebarResizer } from './SidebarResizer.js';
import { switchToBootstrapTabById } from '../utils/jrfuncs.js';
import { DOM_ELEMENTS } from '../constants.js';

const { ID_SIDEBAR, ID_SIDEBAR_TOGGLE, ID_SIDEBAR_TOGGLE_ICON, ID_SIDEBAR_RESIZE_HANDLE,
    ID_SIDEBAR_FULLWIDTH_TOGGLE, ID_SIDEBAR_FULLWIDTH_TOGGLE_ICON,
    ID_MENU_SETTINGS, ID_MENU_TOGGLE_PDF_READER, ID_MENU_BACKGROUND_MUSIC, ID_MENU_WORLD_SELECT, ID_MENU_HELP, ID_MENU_ABOUT,
    ID_LINK_SETTINGS_FROM_ADVANCED, ID_ADVANCED_ENABLED_CONTENT, ID_ADVANCED_DISABLED_CONTENT, CLASS_ADVANCEDONLY_CONTENT,
    ID_SEARCH_TABS, ID_APP_CONTAINER, CLASS_COLLAPSED, CLASS_SIDEBAR_FULLWIDTH } = DOM_ELEMENTS;

// Whether the full-width sidebar state is restored on world load (currently deliberately not).
const OPTION_RESTORE_LAST_FULLWIDTH_STATE = false;



/**
 * Manages sidebar UI, menu interactions, and tab persistence.
 * Handles settings, help, and about modal dialogs.
 * @class
 */
export class UiManager {
    /**
     * Creates a new UiManager instance.
     * @param {Object} mapApp - The MapApp instance
     */
    constructor(mapApp) {
        this.mapApp = mapApp;
        // Listeners on the (static) sidebar chrome are bound exactly once per page; see initialize().
        this.listenersBound = false;
        this.sidebarResizer = null;
    }



    // ==========================================
    // SETUP
    // ==========================================



    /**
     * Initializes the UIManager. Runs on EVERY world load, but one UiManager lives for the whole
     * page (MapApp constructs it once), so this keeps two things apart:
     *  - the sidebar chrome (collapse arrows, menu items, search tabs, resize handle) is static page
     *    DOM and gets its event listeners bound only on the first call. Binding them per world used to
     *    stack one handler per load, and two stacked collapse handlers cancel each other out — which
     *    was exactly the "sidebar arrow buttons do nothing after switching world" bug;
     *  - the sidebar STATE (width, collapsed, active tab, tab visibility) is stored per world and is
     *    re-applied on every call.
     */
    initialize() {
        this.bindListenersOnce();
        this.applyWorldState();
    }


    /**
     * Binds the event listeners of the static sidebar DOM; a no-op after the first call.
     * @private
     */
    bindListenersOnce() {
        if (this.listenersBound) return;
        this.listenersBound = true;
        this.initializeSidebarMenu();
        this.initializeSidebarListeners();
    }


    /**
     * Re-applies the current world's saved sidebar state to the DOM.
     * @private
     */
    applyWorldState() {
        this.restoreSidebarTab();
        this.restoreSidebarState();
        this.restoreSidebarFullWidthState();
        // Advanced Search tab visibility
        this.initializeAdvancedSearchTab();
        // Log Play tab visibility
        this.initializeLogPlayTab();
    }




    /**
     * Attaches click event listeners to sidebar menu items (Settings, Help, About).
     * @private
     */
    initializeSidebarMenu() {
        document.getElementById(ID_MENU_SETTINGS).addEventListener('click', (e) => {
            e.preventDefault();
            this.handleSettingsClick();
        });

        document.getElementById(ID_MENU_TOGGLE_PDF_READER).addEventListener('click', (e) => {
            e.preventDefault();
            this.mapApp.togglePdfViewer();
        });

        document.getElementById(ID_MENU_BACKGROUND_MUSIC)?.addEventListener('click', (e) => {
            e.preventDefault();
            this.mapApp.toggleBackgroundMusicTab();
        });

        document.getElementById(ID_MENU_WORLD_SELECT).addEventListener('click', (e) => {
            e.preventDefault();
            this.handleWorldSelectClick();
        });

        document.getElementById(ID_LINK_SETTINGS_FROM_ADVANCED).addEventListener('click', (e) => {
            e.preventDefault();
            this.handleSettingsClick();
        });

        document.getElementById(ID_MENU_HELP).addEventListener('click', (e) => {
            e.preventDefault();
            this.handleHelpClick();
        });

        document.getElementById(ID_MENU_ABOUT).addEventListener('click', (e) => {
            e.preventDefault();
            this.handleAboutClick();
        });
    }






    /**
     * Binds the sidebar's collapse toggle, search-tab persistence, drag-to-resize and full-width
     * toggle listeners. Bound once per page — the elements are static DOM; see initialize().
     * @private
     */
    initializeSidebarListeners() {
        const sidebar = document.getElementById(ID_SIDEBAR);
        const toggleButton = document.getElementById(ID_SIDEBAR_TOGGLE);

        toggleButton.addEventListener('click', () => {
            const isCollapsed = sidebar.classList.contains(CLASS_COLLAPSED);
            this.setSidebarCollapsed(!isCollapsed);
        });

        // Search tab switches persist the active tab
        this.initializeSidebarTabListeners();

        // Drag-to-resize; the callback keeps the collapsed slide-out margin in step with the new width
        const resizeHandle = document.getElementById(ID_SIDEBAR_RESIZE_HANDLE);
        const onWidthChange = () => {
            if (sidebar.classList.contains(CLASS_COLLAPSED)) {
                const width = sidebar.offsetWidth;
                sidebar.style.marginRight = `-${width}px`;
            }
        };
        this.sidebarResizer = new SidebarResizer(sidebar, resizeHandle, this.getStateManager(), onWidthChange);

        // Full-width toggle button
        this.initializeSidebarFullWidthToggle();
    }


    /**
     * Re-applies the current world's saved sidebar width and collapsed/expanded state.
     * @private
     */
    restoreSidebarState() {
        this.sidebarResizer.restoreWidth();
        this.applySidebarCollapsedToDom(this.getStateManager().getSidebarCollapsed());
    }


    /**
     * Collapses or expands the sidebar and persists the choice for the current world.
     * @param {boolean} collapsed - True to collapse (slide out), false to expand
     */
    setSidebarCollapsed(collapsed) {
        this.applySidebarCollapsedToDom(collapsed);
        this.getStateManager().setSidebarCollapsed(collapsed);
    }


    /**
     * Applies a collapsed/expanded state to the sidebar DOM (class, slide-out margin, arrow icon)
     * without touching persisted state.
     * @private
     * @param {boolean} collapsed - True to collapse (slide out), false to expand
     */
    applySidebarCollapsedToDom(collapsed) {
        const sidebar = document.getElementById(ID_SIDEBAR);
        const toggleIcon = document.getElementById(ID_SIDEBAR_TOGGLE_ICON);
        if (collapsed) {
            const width = sidebar.offsetWidth;
            sidebar.classList.add(CLASS_COLLAPSED);
            sidebar.style.marginRight = `-${width}px`;
            toggleIcon.innerHTML = '&#9664;'; // arrow points back in (click to expand)
        } else {
            sidebar.classList.remove(CLASS_COLLAPSED);
            sidebar.style.marginRight = '0';
            toggleIcon.innerHTML = '&#9654;'; // arrow points out (click to collapse)
        }
    }


    /**
     * Ensures the sidebar is expanded (not collapsed).
     * Used when programmatic actions (e.g. map marker clicks) need the sidebar visible.
     */
    ensureSidebarExpanded() {
        const sidebar = document.getElementById(ID_SIDEBAR);
        if (sidebar && sidebar.classList.contains(CLASS_COLLAPSED)) {
            this.setSidebarCollapsed(false);
        }
    }


    /**
     * Ensures the main panel is visible by collapsing the full-width sidebar if active.
     */
    ensureMainPanelVisible() {
        const appContainer = document.getElementById(ID_APP_CONTAINER);
        if (appContainer && appContainer.classList.contains(CLASS_SIDEBAR_FULLWIDTH)) {
            this.setSidebarFullWidth(false);
        }
    }




    /**
     * Binds the full-width sidebar toggle button's click handler (once per page; see initialize()).
     * @private
     */
    initializeSidebarFullWidthToggle() {
        const fullWidthToggle = document.getElementById(ID_SIDEBAR_FULLWIDTH_TOGGLE);
        const appContainer = document.getElementById(ID_APP_CONTAINER);

        fullWidthToggle.addEventListener('click', () => {
            const isFullWidth = appContainer.classList.contains(CLASS_SIDEBAR_FULLWIDTH);
            this.setSidebarFullWidth(!isFullWidth);
        });
    }


    /**
     * Restores the persisted full-width state, when OPTION_RESTORE_LAST_FULLWIDTH_STATE allows it.
     * @private
     */
    restoreSidebarFullWidthState() {
        if (!OPTION_RESTORE_LAST_FULLWIDTH_STATE) return;
        const isFullWidth = this.getStateManager().getSidebarFullWidth();
        if (isFullWidth) {
            const appContainer = document.getElementById(ID_APP_CONTAINER);
            const fullWidthIcon = document.getElementById(ID_SIDEBAR_FULLWIDTH_TOGGLE_ICON);
            appContainer.classList.add(CLASS_SIDEBAR_FULLWIDTH);
            fullWidthIcon.innerHTML = '&#9654;'; // Right arrow (click to shrink back)
        }
    }


    /**
     * Sets the sidebar full-width state.
     * @param {boolean} fullWidth - Whether to enable full-width mode
     */
    setSidebarFullWidth(fullWidth) {
        const appContainer = document.getElementById(ID_APP_CONTAINER);
        const fullWidthIcon = document.getElementById(ID_SIDEBAR_FULLWIDTH_TOGGLE_ICON);

        if (fullWidth) {
            appContainer.classList.add(CLASS_SIDEBAR_FULLWIDTH);
            fullWidthIcon.innerHTML = '&#9654;'; // Right arrow (click to shrink back)
        } else {
            appContainer.classList.remove(CLASS_SIDEBAR_FULLWIDTH);
            fullWidthIcon.innerHTML = '&#9664;'; // Left arrow (click to expand)
        }
        this.getStateManager().setSidebarFullWidth(fullWidth);
    }


    /**
     * Shows or hides the Advanced Search tab content based on user settings.
     * When disabled, displays a message explaining the feature is for case authors only.
     * @private
     */
    initializeAdvancedSearchTab() {
        const advancedSearchEnabled = this.getStateManager().getAdvancedSearchEnabled();
        UiManager.adjustAdvancedSearchTabVisibility(advancedSearchEnabled);
    }

    /**
     * Shows or hides the Log Play tab based on user settings.
     * @private
     */
    initializeLogPlayTab() {
        const hideLogPanel = this.getStateManager().getHideLogPanel();
        UiManager.adjustVisibilityOfLogPlayPanel(!hideLogPanel);
        UiManager.adjustPlayLogFontSize(this.getStateManager().getPlayLogFontSize());
    }
    









    // ==========================================
    // ACCESSORS
    // ==========================================

    /**
     * Gets the StateManager instance from mapApp.
     * @returns {Object} The StateManager instance
     */
    getStateManager() {
        return this.mapApp.getStateManager();
    }






    // ==========================================
    // MAIN METHODS
    // ==========================================



    /**
     * Adjusts the visibility of advanced search tab content based on enabled state.
     * @static
     * @param {boolean} enabled - Whether advanced search is enabled
     */
    static adjustAdvancedSearchTabVisibility(enabled) {
        const enabledContent = document.getElementById(ID_ADVANCED_ENABLED_CONTENT);
        const disabledContent = document.getElementById(ID_ADVANCED_DISABLED_CONTENT);

        // tabs
        if (enabledContent && disabledContent) {
            if (enabled) {
                enabledContent.style.display = '';
                disabledContent.style.display = 'none';
            } else {
                enabledContent.style.display = 'none';
                disabledContent.style.display = '';
            }
        }

        // find all elements with class CLASS_ADVANCEDONLY_CONTENT and set them to display none or ''
        const advancedElements = document.querySelectorAll('.' + CLASS_ADVANCEDONLY_CONTENT);
        advancedElements.forEach((el) => {
            el.style.display = enabled ? '' : 'none';
        });
    }


    /**
     * Adjusts the visibility of the Log Play tab and pane based on the visible flag.
     * @static
     * @param {boolean} visible - Whether the Log Play panel should be visible
     */
    static adjustVisibilityOfLogPlayPanel(visible) {
        const tab = document.getElementById('logplay-tab');
        const pane = document.getElementById('logplay-pane');

        if (tab) {
            const tabItem = tab.closest('li.nav-item');
            if (tabItem) {
                tabItem.style.display = visible ? '' : 'none';
            }
        }
        if (pane) {
            pane.style.display = visible ? '' : 'none';
        }
    }


    /**
     * Sets the font size of the Play Log textarea.
     * @static
     * @param {number} size - Font size in points (matches the unit used in sidebar.css)
     */
    static adjustPlayLogFontSize(size) {
        if (!Number.isFinite(size)) return;
        const textarea = document.getElementById('logplay-textarea');
        if (textarea) {
            textarea.style.fontSize = `${size}pt`;
        }
        // The search-highlight backdrop must match the textarea's font size, or
        // the <mark> highlights drift off the text (and wrap differently, so the
        // drift grows down the log). Keep them in lockstep.
        const backdrop = document.getElementById('logplay-highlight-backdrop');
        if (backdrop) {
            backdrop.style.fontSize = `${size}pt`;
        }
    }









    /**
     * Binds the search-tab switch listener that persists the active sidebar tab (once per page).
     * Must be bound BEFORE restoreSidebarTab() runs, so that updateTabCaptions is called after
     * the restored tab's transition completes — initialize() guarantees that order.
     * @private
     */
    initializeSidebarTabListeners() {
        const tabButtons = document.querySelectorAll('#' + ID_SEARCH_TABS + ' button[data-bs-toggle="tab"]');
        tabButtons.forEach(button => {
            button.addEventListener('shown.bs.tab', (event) => {
                const activeTabId = event.target.id;
                this.getStateManager().setSidebarTab(activeTabId);
                this.updateTabCaptions();
                // Update overlay layers visibility when tab changes
                if (this.mapApp) {
                    this.mapApp.onTabChanges(activeTabId);
                }
            });
        });
    }


    /**
     * Switches to the current world's saved sidebar tab.
     * @private
     */
    restoreSidebarTab() {
        // Note: updateTabCaptions() will be called by the shown.bs.tab listener (see
        // initializeSidebarTabListeners) after the tab transition completes. Do NOT call it
        // synchronously here as that can interfere with Bootstrap's tab transition and cause
        // content panes to stop switching.
        // Only an id naming one of the sidebar's own tab buttons is restored. A stored id is any string
        // (a hand-edited backup, a tab a later version dropped): one naming an element outside a
        // tablist made Bootstrap's Tab.show() throw, which failed every load of that world, and one
        // naming another tablist's button would have switched that one instead.
        const savedTabId = this.getStateManager().getSidebarTab();
        const savedTabButton = savedTabId ? document.getElementById(savedTabId) : null;
        const isSidebarTabButton = !!savedTabButton
            && savedTabButton.matches('button[data-bs-toggle="tab"]')
            && !!savedTabButton.closest('#' + ID_SEARCH_TABS);
        if (isSidebarTabButton) {
            switchToBootstrapTabById(savedTabId);
        } else {
            // No usable saved tab - update captions for the default active tab
            this.updateTabCaptions();
        }
    }



    /**
     * Updates tab captions based on active state.
     * Active tabs show their full caption, inactive tabs show abbreviated caption.
     * Reads data-caption-short and data-caption-full attributes from tab buttons.
     * @private
     */
    updateTabCaptions() {
        const tabButtons = document.querySelectorAll('#' + ID_SEARCH_TABS + ' button[data-bs-toggle="tab"]');
        tabButtons.forEach(button => {
            const isActive = button.classList.contains('active');
            const shortCaption = button.dataset.captionShort;
            const fullCaption = button.dataset.captionFull;

            if (shortCaption && fullCaption) {
                button.textContent = isActive ? fullCaption : shortCaption;
            }
        });
    }







    // ==========================================
    // UI TRIGGERS
    // ==========================================


    /**
     * Opens the settings modal dialog.
     */
    handleSettingsClick() {
        this.mapApp.showSettingsDialog();
    }


    /**
     * Opens the help modal dialog, optionally navigating to a specific topic.
     * @param {string|null} [topicId=null] - Topic ID to display (e.g., 'about', 'search')
     * @param {string|null} [anchor=null] - Anchor within the topic to scroll to
     */
    handleHelpClick(topicId = null, anchor = null) {
        this.mapApp.showHelpDialogAtTopic(topicId, anchor);
    }


    /**
     * Opens the help modal to the "About" topic.
     * @private
     */
    handleAboutClick() {
        this.handleHelpClick('about');
    }


    /**
     * Opens the world selection dialog.
     * @private
     */
    handleWorldSelectClick() {
        this.mapApp.showWorldSelectDialog();
    }











}
